import { imgUsKitsGolf } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Button, Image, Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import React, { memo } from 'react';
import { StyleSheet } from 'react-native';
import type { HomeStep, WelcomeBannerProps } from './types';

// ─── Step content data ────────────────────────────────────────────────────────

type StepContent = {
  subtitle: (playerName?: string) => string;
  ctaLabel: string;
};

const STEP_CONTENT: Record<HomeStep, StepContent> = {
  1: {
    subtitle: () =>
      'Add your first golfer to unlock lessons, the Player Pathway, and tournaments—all in one place.',
    ctaLabel: 'Add a Player',
  },
  2: {
    subtitle: (name = 'Your player') =>
      `${name} is all set up! Book a first lesson to start using the Player Pathway.`,
    ctaLabel: 'Book a Session',
  },
  3: {
    subtitle: (name = 'Your player') =>
      `Great progress! Track ${name}'s pins and levels on the Player Pathway.`,
    ctaLabel: 'Track learning progress',
  },
};

// ─── Root component ───────────────────────────────────────────────────────────

function WelcomeBanner({ currentStep, playerName, onCtaPress }: WelcomeBannerProps) {
  const { scale, isTablet } = useScaling();
  const styles = useStyles();
  const { subtitle, ctaLabel } = STEP_CONTENT[currentStep];

  return (
    <Stack style={styles.card}>
      <Stack paddingTop={scale(12)} paddingX={scale(isTablet ? 20 : 16)}>
        <Image
          source={imgUsKitsGolf}
          width="100%"
          height={scale({ phone: 180, tablet: 180 })}
          resizeMode="contain"
          style={styles.logo}
        />
        <Text
          variant="Font Guardian App/Heading/Heading H6 18px"
          style={styles.title}
          numberOfLines={2}>
          Welcome to U.S. Kids Golf
        </Text>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
          style={styles.subtitle}
          numberOfLines={3}>
          {subtitle(playerName)}
        </Text>
      </Stack>

      {/* CTA */}
      <Stack
        paddingTop={scale(16)}
        paddingBottom={scale(isTablet ? 20 : 16)}
        paddingX={scale(isTablet ? 20 : 16)}>
        <Button onPress={onCtaPress} style={styles.cta}>
          <Button.Text fontWeight="700">{ctaLabel}</Button.Text>
        </Button>
      </Stack>
    </Stack>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = createStyles(({ scale }) => ({
  card: {
    backgroundColor: colors['0'],
    borderRadius: scale(16),
    borderCurve: 'continuous',
    overflow: 'hidden',
    shadowColor: '#D5D9E2',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 2,
  },
  imageRow: {
    paddingTop: scale(16),
    height: scale(130),
    backgroundColor: guardianColors['1'],
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: guardianColors['3'],
  },
  logo: {
    marginRight: scale(-12),
    marginBottom: scale(4),
  },
  golferImage: {
    marginBottom: 0,
  },
  title: {
    color: colors['10'],
    marginBottom: scale(6),
  },
  subtitle: {
    color: guardianColors['10'],
  },
  cta: {
    width: '100%',
    borderRadius: scale(12),
  },
}));

export type { HomeStep, WelcomeBannerProps };
export default memo(WelcomeBanner);
